import React, {Fragment, useEffect, useMemo, useState} from "react";
import {Link, useHistory, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import {createWorkflowApi, getWorkflowApi, getWorkflowFieldsApi, updateWorkflowApi} from "../../services/tagsAutomationService";
import Select from "react-select";
import Switch from "react-switch";
import {Card, CardBody, Col, Container, FormGroup, Input, Label, Row} from "reactstrap";
import Breadcrumbs from "../../layout/breadcrumb";
import {BASE, ConditionLine, errorMessage, number, RequirePermission, TagBadge, TagList, TagsField, useCan, usePreview} from "./common";
import ConditionPicker from "./conditionPicker";
import WorkflowDialog from "./workflowDialog";
import "./tagsAutomation.scss";

const STATUSES = [
    {value: 'active', label: 'Active'},
    {value: 'paused', label: 'Paused'},
];

const VARIANT_MATCH = [
    {value: 'any', label: 'Any of the variants'},
    {value: 'all', label: 'All of the variants'},
];

// react-select in the theme's form-control look; red border when the server rejected the value.
const selectStyles = (invalid) => ({
    control: (base) => ({...base, minHeight: 38, ...(invalid ? {borderColor: '#dc3545'} : {})}),
    menuPortal: (base) => ({...base, zIndex: 1060}),
});

const EMPTY = {
    name: '',
    status: 'active',
    trigger_events: ['created', 'updated', 'stock', 'price'],
    delay_minutes: 0,
    match_type: 'AND',
    conditions: [],
    add_tags: [],
    auto_remove: true,
    remove_tags: [],
    remove_patterns: [],
    remove_after_days: null,
};

/** "instantly when a product is created, updated or sold, after a price import, and every night at 03:45 Europe/London" */
const runsText = (events, delay, delays) => {
    const label = (delays.find(d => d.value === delay) || {}).label || '';
    const lead = delay === 0 ? 'when' : label.replace('After ', '').toLowerCase() + ' after';
    const product = [];
    if (events.includes('created')) product.push('created');
    if (['updated', 'stock', 'price'].some(e => events.includes(e))) product.push('updated or sold');
    const clauses = [];
    if (product.length) clauses.push(`${lead} a product is ${product.join(', ')}`);
    clauses.push('every night at 03:45 Europe/London');
    const text = clauses.length > 1 ? clauses.slice(0, -1).join(', ') + ', and ' + clauses[clauses.length - 1] : clauses[0];
    return (delay === 0 && clauses.length > 1 ? 'instantly ' : '') + text;
};

/**
 * GOD-TASK-197 Tags Automation › Create / Edit workflow (prototype screen 2).
 */
const WorkflowForm = () => {
    const {id} = useParams();
    const can = useCan();
    const history = useHistory();
    const [form, setForm] = useState(EMPTY);
    const [original, setOriginal] = useState(null);
    const [meta, setMeta] = useState(null);
    const [schedule, setSchedule] = useState(false);
    const [days, setDays] = useState(30);
    const [errors, setErrors] = useState({});
    const [picker, setPicker] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pauseDialog, setPauseDialog] = useState(null);

    useEffect(() => {
        getWorkflowFieldsApi().then(setMeta).catch(e => toast.error(errorMessage(e), {position: toast.POSITION.TOP_RIGHT}));
    }, []);

    useEffect(() => {
        // The route stays mounted for a moment after leaving with no params (exit transition).
        if (!id) return;
        getWorkflowApi(id).then(w => {
            setOriginal(w);
            setForm({
                name: w.name,
                status: w.status,
                trigger_events: w.trigger_events,
                delay_minutes: w.delay_minutes,
                match_type: w.match_type,
                conditions: w.conditions,
                add_tags: w.add_tags,
                auto_remove: w.auto_remove,
                remove_tags: w.remove_tags,
                remove_patterns: w.remove_patterns || [],
                remove_after_days: w.remove_after_days,
            });
            setSchedule(!!w.remove_after_days);
            setDays(w.remove_after_days || 30);
        }).catch(e => toast.error(errorMessage(e), {position: toast.POSITION.TOP_RIGHT}));
    }, [id]);

    const fieldsByKey = useMemo(() => {
        const map = {};
        (meta?.groups || []).forEach(g => g.fields.forEach(f => {
            map[f.key] = {...f, group: g.name};
        }));
        return map;
    }, [meta]);

    // Editing a field clears its server error, as the other forms do.
    const clearErrors = (...keys) => setErrors(e => {
        const next = {...e};
        Object.keys(next).forEach(k => keys.some(p => k === p || k.startsWith(p + '.')) && delete next[k]);
        return next;
    });
    const set = (key, value) => {
        setForm(f => ({...f, [key]: value}));
        const tagKeys = ['add_tags', 'remove_tags', 'remove_patterns'];
        clearErrors(...(tagKeys.includes(key) ? tagKeys : [key]));
    };
    const setCondition = (index, patch) => {
        setForm(f => ({...f, conditions: f.conditions.map((c, i) => (i === index ? {...c, ...patch} : c))}));
        clearErrors(`conditions.${index}`);
    };
    const err = (key) => errors[key] ? <div className="invalid-feedback d-block">{errors[key][0]}</div> : null;

    const definition = {
        match_type: form.match_type,
        conditions: form.conditions.map(c => ({field: c.field, operator: c.operator, value: c.value, variant_match: c.variant_match})),
        add_tags: form.add_tags,
        remove_tags: form.remove_tags,
        remove_patterns: form.remove_patterns,
        auto_remove: form.auto_remove,
        workflow_id: id || null,
    };
    const definitionReady = (form.add_tags.length > 0 || form.remove_tags.length > 0 || form.remove_patterns.length > 0)
        && form.conditions.every(c => !fieldsByKey[c.field] || !(fieldsByKey[c.field].operators.find(o => o.value === c.operator) || {}).needs_value || (c.value !== null && String(c.value).trim() !== ''));
    const preview = usePreview(definition, !!meta && definitionReady);

    const save = (runNow, pauseAction) => {
        const payload = {
            ...form,
            conditions: definition.conditions,
            remove_after_days: schedule ? Number(days) : null,
            run_now: runNow,
            pause_action: pauseAction,
        };
        setSaving(true);
        setErrors({});
        (id ? updateWorkflowApi(id, payload) : createWorkflowApi(payload))
            .then(res => {
                toast.success(`Workflow ${res.workflow.name} saved.${runNow ? ' Run started; results appear in the Activity Log.' : ''}`, {position: toast.POSITION.TOP_RIGHT});
                history.push(`${BASE}/workflows`);
            })
            .catch(e => {
                // A 422 is already toasted by utils/api; its field messages go under the fields.
                if (e?.errors) {
                    setErrors(e.errors);
                } else {
                    toast.error(errorMessage(e), {position: toast.POSITION.TOP_RIGHT});
                }
                window.scrollTo(0, 0);
            })
            .finally(() => setSaving(false));
    };

    const submit = (runNow) => {
        // Pausing from the form asks Keep or Remove, as the Pause button does.
        if (id && original && original.status === 'active' && form.status === 'paused') {
            setPauseDialog({runNow});
            return;
        }
        save(runNow);
    };


    const andOr = (value, title, help) => (
        <Col md={6}>
            <Card className={`mb-0 ${form.match_type === value ? 'border-primary' : ''}`} style={{cursor: 'pointer'}} onClick={() => set('match_type', value)}>
                <CardBody className="p-3">
                    <FormGroup check className="m-0">
                        <Input type="radio" name="match_type" id={`match-${value}`} checked={form.match_type === value} onChange={() => set('match_type', value)}/>
                        <Label check for={`match-${value}`} className="f-w-600 mb-0">{title}</Label>
                        <div className="f-12 text-muted">{help}</div>
                    </FormGroup>
                </CardBody>
            </Card>
        </Col>
    );

    const conditionValue = (c, i) => {
        const def = fieldsByKey[c.field];
        const op = def && def.operators.find(o => o.value === c.operator);
        const invalid = !!errors[`conditions.${i}.value`];
        if (op && !op.needs_value) {
            return <span className="f-12 text-muted">No value needed</span>;
        }
        if (def && def.type === 'enum' && !['in', 'not_in'].includes(c.operator)) {
            return (
                <Select className={invalid ? 'is-invalid' : ''} placeholder="Choose…" styles={selectStyles(invalid)} menuPortalTarget={document.body}
                        options={def.options} value={def.options.find(o => o.value === c.value) || null}
                        onChange={o => setCondition(i, {value: o.value, value_label: o.label})}
                        components={{IndicatorSeparator: () => null}}/>
            );
        }
        return (
            <Input type="text" invalid={invalid} value={c.value || ''}
                   onChange={e => setCondition(i, {value: e.target.value, value_label: e.target.value})}/>
        );
    };

    const title = id ? 'Edit Workflow' : 'Create Workflow';
    const dataMenu = [
        {type: 1, text: 'Tags Automation', link: '/tags-automation/workflows'},
        {type: 0, text: id ? (original?.name || '…') : 'Create'},
    ];
    const autoNote = form.auto_remove ? '(auto-removed when conditions stop matching)' : '(kept even if conditions stop matching)';
    const delays = (meta?.delays || []).map(d => ({value: d.value, label: d.label}));

    // As the order list filters.
    const switchProps = {uncheckedIcon: false, checkedIcon: false, className: "react-switch"};

    return (
        <Fragment>
            <Breadcrumbs title={title} data={dataMenu}/>
            <Container fluid={true} className="product-wrapper">
                <Card>
                    <CardBody>
                        <h5 className="mb-3">Workflow</h5>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="form-label">Name</Label>
                                    <Input type="text" invalid={!!errors.name} value={form.name} onChange={e => set('name', e.target.value)}/>
                                    {err('name')}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="form-label">Status</Label>
                                    <Select styles={selectStyles(false)} options={STATUSES} value={STATUSES.find(o => o.value === form.status)}
                                            onChange={o => set('status', o.value)} components={{IndicatorSeparator: () => null}}/>
                                </FormGroup>
                            </Col>
                        </Row>
                        <p className="f-12 text-muted mb-1">Pausing or deleting a workflow asks whether its tags should be removed from the products that currently carry them. Choose No to leave the tags in place and only stop future evaluation.</p>
                        <p className="f-12 text-muted mb-0">Applies to products. Order, customer and checkout workflows are not supported in the marketplace.</p>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <h5 className="mb-2">When should this run?</h5>
                        <p className="f-12 text-muted">"Updated or sold" covers product edits, stock changes (sales, restocks, stock imports), price imports and sale offers. Tick the events that should re-evaluate the conditions. The nightly check always runs for every active workflow, so a tag that depends only on time (for example “published more than 2 months ago”) still updates.</p>
                        <Row className="mb-3">
                            {(meta?.events || []).map(ev => (
                                <Col md={6} key={ev.value}>
                                    <FormGroup check className="mb-2">
                                        {/* One box can stand for several engine events ("updated or sold" = updated, stock, price). */}
                                        <Input type="checkbox" id={`event-${ev.value}`} checked={(ev.events || [ev.value]).some(x => form.trigger_events.includes(x))}
                                               onChange={e => {
                                                   const group = ev.events || [ev.value];
                                                   const rest = form.trigger_events.filter(x => !group.includes(x));
                                                   set('trigger_events', e.target.checked ? [...rest, ...group] : rest);
                                               }}/>
                                        <Label check for={`event-${ev.value}`}>{ev.label}</Label>
                                    </FormGroup>
                                </Col>
                            ))}
                            <Col md={6}>
                                <FormGroup check className="mb-2">
                                    <Input type="checkbox" id="event-nightly" checked disabled/>
                                    <Label check for="event-nightly">Every night at 03:45 Europe/London (always on)</Label>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row className="align-items-end">
                            <Col md={4}>
                                <FormGroup className="mb-0">
                                    <Label className="form-label">Run the workflow</Label>
                                    <Select styles={selectStyles(false)} options={delays} value={delays.find(d => d.value === form.delay_minutes) || null}
                                            onChange={o => set('delay_minutes', Number(o.value))} components={{IndicatorSeparator: () => null}}/>
                                </FormGroup>
                            </Col>
                            <Col md={8}>
                                <p className="f-12 text-muted mb-2">A delay is useful when several updates land together, for example a stock import.</p>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <h5 className="mb-3">Conditions</h5>
                        <Row className="mb-3">
                            {andOr('AND', 'AND', 'All the conditions must match')}
                            {andOr('OR', 'OR', 'If any of the conditions match')}
                        </Row>
                        {form.conditions.length === 0 && <p className="f-12 text-muted">No conditions yet: the workflow applies to every product.</p>}
                        {form.conditions.map((c, i) => {
                            const def = fieldsByKey[c.field];
                            const operators = (def?.operators || [{value: c.operator, label: c.operator_label}]).map(o => ({value: o.value, label: o.label}));
                            const opInvalid = !!errors[`conditions.${i}.operator`];
                            return (
                                <div key={i} className="border-bottom py-3">
                                    <Row className="align-items-center">
                                        <Col md={1} className="f-12 f-w-600 text-muted">{i === 0 ? 'IF' : `${form.match_type} IF`}</Col>
                                        <Col md={3}>
                                            <div className="f-w-500">{c.field_label}</div>
                                            <div className="f-12 text-muted">{c.group}</div>
                                            {(def?.is_variant || c.variant_match) && (
                                                <div className="mt-2">
                                                    <Select styles={selectStyles(false)} menuPortalTarget={document.body} options={VARIANT_MATCH}
                                                            value={VARIANT_MATCH.find(o => o.value === (c.variant_match || 'any'))}
                                                            onChange={o => setCondition(i, {variant_match: o.value})}
                                                            components={{IndicatorSeparator: () => null}}/>
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={3}>
                                            <Select className={opInvalid ? 'is-invalid' : ''} styles={selectStyles(opInvalid)} menuPortalTarget={document.body}
                                                    options={operators} value={operators.find(o => o.value === c.operator) || null}
                                                    onChange={o => setCondition(i, {operator: o.value, operator_label: o.label})}
                                                    components={{IndicatorSeparator: () => null}}/>
                                        </Col>
                                        <Col md={4}>{conditionValue(c, i)}</Col>
                                        <Col md={1} className="text-right">
                                            <button type="button" className="btn btn-link p-0 mx-1" title="Duplicate condition" aria-label="Duplicate condition"
                                                    onClick={() => set('conditions', [...form.conditions.slice(0, i + 1), {...c}, ...form.conditions.slice(i + 1)])}>
                                                <i className="fa fa-copy" style={{fontSize: 16, color: '#7366ff'}}/>
                                            </button>
                                            <button type="button" className="btn btn-link p-0 mx-1" title="Remove condition" aria-label="Remove condition"
                                                    onClick={() => set('conditions', form.conditions.filter((x, j) => j !== i))}>
                                                <i className="fa fa-trash" style={{fontSize: 16, color: '#e4566e'}}/>
                                            </button>
                                        </Col>
                                    </Row>
                                    {['field', 'operator', 'value'].map(k => <Fragment key={k}>{err(`conditions.${i}.${k}`)}</Fragment>)}
                                </div>
                            );
                        })}
                        <div className="d-flex align-items-center flex-wrap mt-3" style={{gap: 10}}>
                            <button type="button" className="btn btn-sm btn-outline-primary" disabled={!meta} onClick={() => setPicker(true)}><i className="fa fa-plus"/> Add condition</button>
                            <span className="f-12 text-muted">Value hints: numbers accept plain digits, dates accept “2 months ago” or a fixed date, lists accept comma-separated values.</span>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <h5 className="mb-3">Actions when conditions match</h5>
                        <FormGroup>
                            <Label className="form-label">Add tags</Label>
                            <TagsField tags={form.add_tags} options={meta?.tags || []} onChange={v => set('add_tags', v)} placeholder="Search tags, or type a new one and press Enter" invalid={!!errors.add_tags}/>
                            {err('add_tags')}
                        </FormGroup>
                        <div className="d-flex align-items-center mb-1" style={{gap: 10}}>
                            <Switch {...switchProps} checked={form.auto_remove} onChange={checked => set('auto_remove', checked)} aria-label="Auto remove when conditions stop matching"/>
                            <Label className="form-label mb-0">Remove these tags again when the conditions stop matching</Label>
                        </div>
                        <p className="f-12 text-muted">Keep this on for state tags like <i>low stock</i> and <i>sale</i>. Turn it off for one-way tags you want to keep.</p>
                        <hr/>
                        <FormGroup>
                            <Label className="form-label">Remove tags</Label>
                            <TagsField tags={form.remove_tags} options={meta?.tags || []} onChange={v => set('remove_tags', v)} placeholder="Search tags, or type new ones (comma-separated) and press Enter" invalid={!!errors.remove_tags}/>
                            {err('remove_tags')}
                        </FormGroup>
                        <FormGroup>
                            <Label className="form-label">Remove tags matching patterns</Label>
                            <TagsField tags={form.remove_patterns} split={false} onChange={v => set('remove_patterns', v)} placeholder="Type a regular expression, e.g. ^prefix-.*$, and press Enter" invalid={!!errors.remove_patterns}/>
                            {err('remove_patterns')}
                            <p className="f-12 text-muted mt-1 mb-0">Every tag matching a pattern is removed (case-insensitive), e.g. <code>^price-</code> removes every tag starting with "price-".</p>
                        </FormGroup>
                        <p className="f-12 text-muted mb-1">Removed every time the product is evaluated while the conditions match. Generated tags such as categories return automatically once the product stops matching.</p>
                        <p className="f-12 text-muted">Protected tags cannot be added or removed by any workflow, because they decide whether a product can be returned: {(meta?.protected_tags || []).join(', ')}.</p>
                        <hr/>
                        <div className="d-flex align-items-center flex-wrap" style={{gap: 10}}>
                            <Switch {...switchProps} checked={schedule} onChange={setSchedule} aria-label="Schedule removal"/>
                            <Label className="form-label mb-0">Remove the added tags automatically after</Label>
                            <Input type="number" min="1" invalid={!!errors.remove_after_days} value={days} style={{width: 100}} onChange={e => {
                                setDays(e.target.value);
                                clearErrors('remove_after_days');
                            }} disabled={!schedule}/>
                            <Label className="form-label mb-0">days</Label>
                        </div>
                        {err('remove_after_days')}
                    </CardBody>
                </Card>

                <Card className="ta-soft">
                    <CardBody>
                        <h5 className="mb-3">Review</h5>
                        <div className="d-flex flex-column" style={{gap: 8}}>
                            <div className="f-12 text-muted">Process products</div>
                            {form.conditions.length === 0 && <div className="f-12">Every product (no conditions)</div>}
                            {form.conditions.map((c, i) => (
                                <ConditionLine key={i} c={{...c, join: i === 0 ? 'IF' : `${form.match_type} IF`, operator_label: (c.operator_label || '').toUpperCase()}}/>
                            ))}
                            <div className="mt-1">Runs <b>{runsText(form.trigger_events, form.delay_minutes, delays)}</b>.</div>
                            {form.add_tags.length > 0 && (
                                <div className="d-flex align-items-center flex-wrap" style={{gap: 6}}>
                                    <span>Add tags:</span>
                                    {form.add_tags.map(t => <TagBadge key={t} tag={t} kind="add"/>)}
                                    <span className="f-12 text-muted font-italic">{autoNote}</span>
                                </div>
                            )}
                            {form.remove_tags.length > 0 && (
                                <div className="d-flex align-items-center flex-wrap" style={{gap: 6}}>
                                    <span>Remove tags:</span>
                                    <TagList tags={form.remove_tags} kind="remove" limit={10}/>
                                </div>
                            )}
                            {form.remove_patterns.length > 0 && (
                                <div className="d-flex align-items-center flex-wrap" style={{gap: 6}}>
                                    <span>Remove tags matching:</span>
                                    <TagList tags={form.remove_patterns} kind="remove" limit={10}/>
                                </div>
                            )}
                            <div className="f-12 text-muted mt-1">
                                Preview on live data:{' '}
                                {!definitionReady ? 'add a tag to add or remove, and complete the conditions, to see how many products match.'
                                    : preview.status === 'done' ? <><b>{number(preview.matching)}</b> products match today, <b>{number(preview.would_change)}</b> would change.</>
                                        : preview.status === 'failed' ? 'could not be calculated.'
                                            : <b>Calculating…</b>}
                                {id && <> <Link to={`${BASE}/workflows/${id}/test`}>Test on one product</Link></>}
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <div className="d-flex justify-content-end flex-wrap mb-4" style={{gap: 8}}>
                    <Link className="btn btn-light" to={`${BASE}/workflows`}>Cancel</Link>
                    {can('Update Workflow') && <button className="btn btn-outline-primary" type="button" disabled={saving || !meta} onClick={() => submit(true)}>Save and run now</button>}
                    <button className="btn btn-primary" type="button" disabled={saving || !meta} onClick={() => submit(false)}>Save workflow</button>
                </div>
            </Container>

            {picker && meta && (
                <ConditionPicker groups={meta.groups} onClose={() => setPicker(false)}
                                 onAdd={c => {
                                     set('conditions', [...form.conditions, c]);
                                     setPicker(false);
                                 }}/>
            )}
            {pauseDialog && original && (
                <WorkflowDialog workflow={{...original, name: form.name}} mode="pause" busy={saving}
                                onCancel={() => setPauseDialog(null)}
                                onConfirm={action => {
                                    const runNow = pauseDialog.runNow;
                                    setPauseDialog(null);
                                    save(runNow, action);
                                }}/>
            )}
        </Fragment>
    );
};

/**
 * Create needs 'Create Workflow', edit needs 'Update Workflow'. Decided once on mount: during the
 * route's exit transition useParams() is empty for a moment and would read as the create page.
 */
const WorkflowFormPage = () => {
    const {id} = useParams();
    const [permission] = useState(id ? 'Update Workflow' : 'Create Workflow');
    return <RequirePermission name={permission}><WorkflowForm/></RequirePermission>;
};

export default WorkflowFormPage;
